// src/Dashboard/pages/user/Absences/hooks/useAbsencesController.js

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  getAdherentsByUser,
  updateBlessures,
} from "../../../../services/adherentServices";

import {
  appendNote,
  buildAbsenceNote,
  buildReturnNote,
  getAbsenceStats,
  getAbsenceStatus,
  getReturnDateWarning,
  isAdherentAbsent,
  normalizeText,
  toInputDate,
} from "../utils/absenceUtils";

export default function useAbsencesController(userId) {
  const [adherents, setAdherents] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedAdherentId, setSelectedAdherentId] = useState("");
  const [isBlessed, setIsBlessed] = useState(true);
  const [absenceType, setAbsenceType] = useState("Absent(e)");
  const [dateReprise, setDateReprise] = useState("");
  const [motifAbsence, setMotifAbsence] = useState("");

  const [saving, setSaving] = useState(false);
  const [marking, setMarking] = useState(false);
  const [search, setSearch] = useState("");

  const loadAdherents = useCallback(async () => {
    if (!userId) {
      setAdherents([]);
      setSelectedAdherentId("");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const data = await getAdherentsByUser(userId);
      const list = Array.isArray(data) ? data : [];

      setAdherents(list);

      setSelectedAdherentId((currentId) => {
        const currentExists = list.some(
          (adherent) => String(adherent.id) === String(currentId),
        );

        if (currentExists) return currentId;

        return list.length > 0 ? String(list[0].id) : "";
      });
    } catch (error) {
      console.error("Erreur récupération adhérents:", error);
      toast.error("Impossible de charger vos adhérents.");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadAdherents();
  }, [loadAdherents]);

  const selectedAdherent = useMemo(
    () =>
      adherents.find(
        (adherent) => String(adherent.id) === String(selectedAdherentId),
      ) || null,
    [adherents, selectedAdherentId],
  );

  useEffect(() => {
    if (!selectedAdherent) return;

    const currentlyAbsent = isAdherentAbsent(selectedAdherent);

    setIsBlessed(currentlyAbsent);
    setDateReprise(
      currentlyAbsent ? toInputDate(selectedAdherent.date_reprise) : "",
    );
    setMotifAbsence("");
    setAbsenceType("Absent(e)");
  }, [selectedAdherent]);

  const filteredAdherents = useMemo(() => {
    const q = normalizeText(search);

    if (!q) return adherents;

    return adherents.filter((adherent) => {
      const text = normalizeText(`${adherent.prenom || ""} ${adherent.nom || ""}`);
      return text.includes(q);
    });
  }, [adherents, search]);

  const stats = useMemo(() => getAbsenceStats(adherents), [adherents]);

  const selectedStatus = useMemo(
    () => (selectedAdherent ? getAbsenceStatus(selectedAdherent) : null),
    [selectedAdherent],
  );

  const returnDateWarning = useMemo(
    () => getReturnDateWarning(dateReprise),
    [dateReprise],
  );

  const updateLocalAdherent = useCallback((adherentId, patch) => {
    setAdherents((previous) =>
      previous.map((adherent) =>
        String(adherent.id) === String(adherentId)
          ? {
              ...adherent,
              ...patch,
            }
          : adherent,
      ),
    );
  }, []);

  const resetForm = useCallback(() => {
    setIsBlessed(true);
    setAbsenceType("Absent(e)");
    setDateReprise("");
    setMotifAbsence("");
  }, []);

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();

      if (!selectedAdherent) {
        toast.warn("Choisissez d’abord un adhérent.");
        return;
      }

      if (!isBlessed) {
        toast.warn(
          "Pour signaler une absence, activez le statut absent. Pour un retour, utilisez le bouton “Marquer comme repris”.",
        );
        return;
      }

      if (!motifAbsence.trim() && !dateReprise) {
        toast.warn("Merci de préciser au moins un motif ou une date de reprise.");
        return;
      }

      const newNote = buildAbsenceNote({
        absenceType,
        dateReprise,
        motifAbsence,
      });

      const newNotes = appendNote(selectedAdherent.notes_adherent, newNote);

      try {
        setSaving(true);

        await updateBlessures(selectedAdherent.id, {
          is_blessed: true,
          date_reprise: dateReprise || null,
          notes_adherent: newNotes,
        });

        updateLocalAdherent(selectedAdherent.id, {
          is_blessed: true,
          date_reprise: dateReprise || null,
          notes_adherent: newNotes,
        });

        toast.success("Absence enregistrée avec succès.");
        setMotifAbsence("");
      } catch (error) {
        console.error(error);
        toast.error("Erreur lors de l’enregistrement.");
      } finally {
        setSaving(false);
      }
    },
    [
      absenceType,
      dateReprise,
      isBlessed,
      motifAbsence,
      selectedAdherent,
      updateLocalAdherent,
    ],
  );

  const handleMarkReturned = useCallback(async () => {
    if (!selectedAdherent) return;

    const ok = window.confirm(
      `Confirmer la reprise pour ${selectedAdherent.prenom} ${selectedAdherent.nom} ?`,
    );

    if (!ok) return;

    const returnNote = buildReturnNote();
    const newNotes = appendNote(selectedAdherent.notes_adherent, returnNote);

    try {
      setMarking(true);

      await updateBlessures(selectedAdherent.id, {
        is_blessed: false,
        date_reprise: null,
        notes_adherent: newNotes,
      });

      updateLocalAdherent(selectedAdherent.id, {
        is_blessed: false,
        date_reprise: null,
        notes_adherent: newNotes,
      });

      setIsBlessed(false);
      setDateReprise("");
      setMotifAbsence("");

      toast.success("Reprise enregistrée.");
    } catch (error) {
      console.error(error);
      toast.error("Impossible d’enregistrer la reprise.");
    } finally {
      setMarking(false);
    }
  }, [selectedAdherent, updateLocalAdherent]);

  return {
    adherents,
    filteredAdherents,
    loading,

    selectedAdherent,
    selectedAdherentId,
    setSelectedAdherentId,

    isBlessed,
    setIsBlessed,

    absenceType,
    setAbsenceType,

    dateReprise,
    setDateReprise,

    motifAbsence,
    setMotifAbsence,

    saving,
    marking,

    search,
    setSearch,

    stats,
    selectedStatus,
    returnDateWarning,

    resetForm,
    handleSubmit,
    handleMarkReturned,
    reload: loadAdherents,
  };
}